import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  Book,
  Building2,
  Users,
  Receipt,
  FolderKanban,
  Clock,
  FileText,
  Settings,
  ChevronRight,
  ArrowLeft,
  Search,
  Mail,
  CreditCard,
  Shield,
  Target,
  Calendar,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'

// Screenshot placeholder component
interface ScreenshotProps {
  id: string
  alt: string
  caption?: string
}

// Guide sections data with screenshot references
const guideSections = [
  {
    id: 'agency-setup',
    title: 'Agency Setup',
    icon: Building2,
    description: 'Configure your agency settings, branding, and master data',
    subsections: [
      {
        id: 'agency-config',
        title: 'Agency Configuration',
        icon: Settings,
        screenshots: ['agency-config-general', 'agency-config-branding'],
        content: `Configure your agency's core information, branding, contact details, and payment settings.`,
        steps: [
          { title: 'Navigate to Agency Config', description: 'Go to Admin → Settings → Agency Config' },
          { title: 'General Settings', description: 'Set agency name, timezone, base currency, and date format' },
          { title: 'Branding', description: 'Upload logo and configure brand colors for the platform' },
          { title: 'Contact & Address', description: 'Add email, phone, website, and physical address' },
          { title: 'Payment Details', description: 'Configure bank account information for invoices' },
          { title: 'Tax Information', description: 'Add GST/Tax registration numbers' },
        ],
      },
      {
        id: 'masters',
        title: 'Master Data',
        icon: FileText,
        screenshots: ['tax-master', 'product-master'],
        content: `Master data contains reusable configurations: taxes, fees, categories, products, payment methods, and exchange rates.`,
        steps: [
          { title: 'Tax Master', description: 'Configure tax rates (GST, VAT, etc.) at Admin → Masters → Tax Master' },
          { title: 'Fees Master', description: 'Set up additional fees at Admin → Masters → Fees Master' },
          { title: 'Category Master', description: 'Create client and expense categories' },
          { title: 'Product Master', description: 'Define products/services with pricing and tax rules' },
          { title: 'Payment Methods', description: 'Add accepted payment options' },
          { title: 'Exchange Rates', description: 'Manage currency conversion rates' },
        ],
      },
      {
        id: 'billing-templates',
        title: 'Billing Templates',
        icon: Receipt,
        screenshots: ['billing-templates-list', 'billing-template-editor'],
        content: `Customize the appearance of your invoices and quotes with branded templates.`,
        steps: [
          { title: 'Access Templates', description: 'Navigate to Admin → Settings → Billing Templates' },
          { title: 'Create Template', description: 'Click Add Template and choose Invoice or Quote type' },
          { title: 'Configure Design', description: 'Set header, footer, logo visibility, and colors' },
          { title: 'Set Default', description: 'Mark a template as default for automatic use' },
          { title: 'Preview', description: 'Preview template with sample data before saving' },
        ],
      },
      {
        id: 'email-templates',
        title: 'Email Templates',
        icon: Mail,
        screenshots: ['email-templates-list', 'email-template-editor'],
        content: `Customize automated emails for invoices, quotes, reminders, and notifications.`,
        steps: [
          { title: 'Access Templates', description: 'Navigate to Admin → Settings → Email Templates' },
          { title: 'Select Template', description: 'Choose from Invoice Sent, Reminder, Quote, Payment, etc.' },
          { title: 'Edit Content', description: 'Modify subject and HTML body with placeholders' },
          { title: 'Use Placeholders', description: 'Insert {{client_name}}, {{invoice_number}}, {{amount}}, etc.' },
          { title: 'Preview & Save', description: 'Preview the email and save changes' },
        ],
      },
    ],
  },
  {
    id: 'clients',
    title: 'Clients',
    icon: Users,
    description: 'Manage your clients and their business entities',
    subsections: [
      {
        id: 'client-management',
        title: 'Client Management',
        icon: Users,
        screenshots: ['clients-list', 'client-form'],
        content: `Create and manage client profiles with contact information and custom fields.`,
        steps: [
          { title: 'View Clients', description: 'Navigate to Clients & Lead → Clients to see all clients' },
          { title: 'Add Client', description: 'Click Add Client and fill in client details' },
          { title: 'Client Categories', description: 'Assign categories for better organization' },
          { title: 'Contact Persons', description: 'Add multiple contact persons per client' },
          { title: 'Custom Fields', description: 'Fill in any custom fields configured for clients' },
          { title: 'Business Entities', description: 'Add multiple business entities under a client' },
        ],
      },
    ],
  },
  {
    id: 'finance',
    title: 'Finance',
    icon: Receipt,
    description: 'Manage invoices, quotes, expenses, and payments',
    subsections: [
      {
        id: 'invoices',
        title: 'Invoices',
        icon: Receipt,
        screenshots: ['invoices-list', 'create-invoice'],
        content: `Create, send, and track invoices with automatic payment reminders.`,
        steps: [
          { title: 'View Invoices', description: 'Navigate to Finances → Invoices to see all invoices' },
          { title: 'Create Invoice', description: 'Click Add Invoice, select client and add line items' },
          { title: 'Apply Tax & Fees', description: 'Add taxes and fees from your master data' },
          { title: 'Send Invoice', description: 'Send invoice via email with payment link' },
          { title: 'Track Payments', description: 'Record partial or full payments received' },
          { title: 'Payment Reminders', description: 'Automatic reminders for overdue invoices' },
        ],
      },
      {
        id: 'quotes',
        title: 'Quotes',
        icon: FileText,
        screenshots: ['quotes-list', 'create-quote'],
        content: `Create professional quotes and convert accepted quotes to invoices.`,
        steps: [
          { title: 'View Quotes', description: 'Navigate to Finances → Quotes to see all quotes' },
          { title: 'Create Quote', description: 'Click Add Quote, select client and add line items' },
          { title: 'Set Validity', description: 'Configure quote expiry date' },
          { title: 'Send Quote', description: 'Send quote to client for approval' },
          { title: 'Convert to Invoice', description: 'Convert accepted quotes to invoices instantly' },
        ],
      },
      {
        id: 'recurring-invoices',
        title: 'Recurring Invoices',
        icon: Calendar,
        screenshots: ['recurring-invoices-list'],
        content: `Set up automatic recurring invoices for retainer clients.`,
        steps: [
          { title: 'Create Recurring', description: 'Navigate to Finances → Recurring Invoices' },
          { title: 'Set Schedule', description: 'Choose frequency: weekly, monthly, quarterly, yearly' },
          { title: 'Configure Details', description: 'Set start date, end date, and invoice content' },
          { title: 'Auto-generation', description: 'Invoices are generated automatically on schedule' },
        ],
      },
      {
        id: 'expenses',
        title: 'Expenses',
        icon: CreditCard,
        screenshots: ['expenses-list', 'expense-form'],
        content: `Track business expenses and link them to clients or projects.`,
        steps: [
          { title: 'View Expenses', description: 'Navigate to Finances → Expenses' },
          { title: 'Add Expense', description: 'Click Add Expense and fill in details' },
          { title: 'Categorize', description: 'Assign expense categories for reporting' },
          { title: 'Attach Receipts', description: 'Upload receipt images or documents' },
          { title: 'Link to Client', description: 'Optionally link expense to a client for billing' },
        ],
      },
    ],
  },
  {
    id: 'projects',
    title: 'Projects',
    icon: FolderKanban,
    description: 'Manage projects with boards, tasks, and team collaboration',
    subsections: [
      {
        id: 'project-management',
        title: 'Project Management',
        icon: FolderKanban,
        screenshots: ['projects-list', 'project-detail'],
        content: `Create projects, set up boards, and manage tasks with your team.`,
        steps: [
          { title: 'View Projects', description: 'Navigate to Delivery → Projects' },
          { title: 'Create Project', description: 'Click Add Project, select client and set details' },
          { title: 'Project Boards', description: 'Create multiple boards per project (Kanban, List, Calendar views)' },
          { title: 'Add Tasks', description: 'Create tasks and assign to team members' },
          { title: 'Track Progress', description: 'Move tasks through workflow stages' },
          { title: 'Time Tracking', description: 'Log time against project tasks' },
        ],
      },
    ],
  },
  {
    id: 'time-tracking',
    title: 'Time Tracking',
    icon: Clock,
    description: 'Track work hours and generate time reports',
    subsections: [
      {
        id: 'time-entries',
        title: 'Time Entries',
        icon: Clock,
        screenshots: ['time-tracking-page'],
        content: `Log time entries against clients and projects for billing and reporting.`,
        steps: [
          { title: 'Access Time Tracking', description: 'Navigate to Delivery → Time Tracking' },
          { title: 'Start Timer', description: 'Click Start to begin tracking time' },
          { title: 'Manual Entry', description: 'Add manual time entries with duration' },
          { title: 'Select Client/Project', description: 'Link time to specific client or project' },
          { title: 'Mark Billable', description: 'Flag entries as billable for invoicing' },
          { title: 'View Reports', description: 'Generate reports at Delivery → Time Reports' },
        ],
      },
    ],
  },
  {
    id: 'crm',
    title: 'CRM & Leads',
    icon: Target,
    description: 'Manage leads, deals, and sales pipeline',
    subsections: [
      {
        id: 'leads-deals',
        title: 'Leads & Deals',
        icon: Target,
        screenshots: ['leads-list', 'deals-kanban'],
        content: `Track leads through your sales pipeline and convert to clients.`,
        steps: [
          { title: 'View Leads', description: 'Navigate to Clients & Lead → CRM → Leads' },
          { title: 'Add Lead', description: 'Create new leads with contact information' },
          { title: 'Create Deal', description: 'Convert leads to deals with value estimates' },
          { title: 'Pipeline View', description: 'Use Kanban board to track deal stages' },
          { title: 'Convert to Client', description: 'Convert won deals to client accounts' },
        ],
      },
    ],
  },
  {
    id: 'contracts',
    title: 'Contracts',
    icon: FileText,
    description: 'Create and manage contracts with e-signing',
    subsections: [
      {
        id: 'contract-management',
        title: 'Contract Management',
        icon: FileText,
        screenshots: ['contracts-list', 'contract-editor'],
        content: `Create contracts from templates and send for electronic signature.`,
        steps: [
          { title: 'View Contracts', description: 'Navigate to Contracts → Contracts' },
          { title: 'Create Template', description: 'Build contract templates at Contracts → Templates' },
          { title: 'Create Contract', description: 'Generate contract from template for a client' },
          { title: 'Add Signers', description: 'Specify who needs to sign the contract' },
          { title: 'Send for Signing', description: 'Send contract via email for e-signature' },
          { title: 'Track Status', description: 'Monitor signing progress and download signed copies' },
        ],
      },
    ],
  },
  {
    id: 'user-access',
    title: 'User Access Management',
    icon: Shield,
    description: 'Manage users, roles, and permissions',
    subsections: [
      {
        id: 'users-roles',
        title: 'Users & Roles',
        icon: Shield,
        screenshots: ['users-list', 'roles-list'],
        content: `Create user accounts and define role-based access permissions.`,
        steps: [
          { title: 'View Users', description: 'Navigate to Admin → User Access → Users' },
          { title: 'Add User', description: 'Invite new team members with email' },
          { title: 'Assign Role', description: 'Assign roles to control access levels' },
          { title: 'Create Role', description: 'Define custom roles at Admin → User Access → Roles' },
          { title: 'Set Permissions', description: 'Configure view/edit permissions per screen' },
        ],
      },
    ],
  },
]

// Screenshot component with placeholder
function Screenshot({ id, alt, caption }: ScreenshotProps) {
  const imagePath = `/screenshots/${id}.png`

  return (
    <figure className="my-4">
      <div className="border rounded-lg overflow-hidden bg-muted/30">
        <img
          src={imagePath}
          alt={alt}
          className="w-full h-auto"
          onError={(e) => {
            // Show placeholder if image not found
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
            target.parentElement!.innerHTML = `
              <div class="flex items-center justify-center h-48 bg-muted/50 text-muted-foreground">
                <span>Screenshot: ${id}</span>
              </div>
            `
          }}
        />
      </div>
      {caption && (
        <figcaption className="text-sm text-muted-foreground text-center mt-2">
          {caption}
        </figcaption>
      )}
    </figure>
  )
}

export default function UserGuidePage() {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSection, setSelectedSection] = useState<string | null>(null)
  const [selectedSubsection, setSelectedSubsection] = useState<{
    sectionId: string
    subsectionId: string
  } | null>(null)

  // Filter sections based on search
  const filteredSections = guideSections.filter((section) => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      section.title.toLowerCase().includes(searchLower) ||
      section.description.toLowerCase().includes(searchLower) ||
      section.subsections.some(
        (sub) =>
          sub.title.toLowerCase().includes(searchLower) ||
          sub.content.toLowerCase().includes(searchLower)
      )
    )
  })

  // Get current subsection content
  const currentSubsection = selectedSubsection
    ? guideSections
        .find((s) => s.id === selectedSubsection.sectionId)
        ?.subsections.find((sub) => sub.id === selectedSubsection.subsectionId)
    : null

  const currentSection = selectedSubsection
    ? guideSections.find((s) => s.id === selectedSubsection.sectionId)
    : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/support">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Book className="h-8 w-8" />
              {t('userGuide.title', 'User Guide')}
            </h1>
            <p className="text-muted-foreground">
              {t('userGuide.subtitle', 'Learn how to use Blueprint Desk effectively')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar Navigation */}
        <Card className="w-80 shrink-0">
          <CardHeader className="pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('userGuide.search', 'Search guide...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-280px)]">
              <Accordion
                type="single"
                collapsible
                value={selectedSection || undefined}
                onValueChange={(value) => setSelectedSection(value || null)}
                className="px-4 pb-4"
              >
                {filteredSections.map((section) => {
                  const Icon = section.icon
                  return (
                    <AccordionItem key={section.id} value={section.id}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <span>{section.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-1 pl-6">
                          {section.subsections.map((sub) => {
                            const SubIcon = sub.icon
                            const isActive =
                              selectedSubsection?.sectionId === section.id &&
                              selectedSubsection?.subsectionId === sub.id
                            return (
                              <button
                                key={sub.id}
                                onClick={() =>
                                  setSelectedSubsection({
                                    sectionId: section.id,
                                    subsectionId: sub.id,
                                  })
                                }
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                                  isActive
                                    ? 'bg-primary text-primary-foreground'
                                    : 'hover:bg-muted'
                                }`}
                              >
                                <SubIcon className="h-3.5 w-3.5" />
                                <span>{sub.title}</span>
                              </button>
                            )
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )
                })}
              </Accordion>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Main Content */}
        <Card className="flex-1">
          <ScrollArea className="h-[calc(100vh-200px)]">
            {currentSubsection && currentSection ? (
              <div className="p-6">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <span>{currentSection.title}</span>
                  <ChevronRight className="h-4 w-4" />
                  <span className="text-foreground font-medium">
                    {currentSubsection.title}
                  </span>
                </div>

                {/* Title */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <currentSubsection.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold">{currentSubsection.title}</h2>
                </div>

                {/* Description */}
                <p className="text-muted-foreground mb-6">{currentSubsection.content}</p>

                {/* First Screenshot */}
                {currentSubsection.screenshots?.[0] && (
                  <Screenshot
                    id={currentSubsection.screenshots[0]}
                    alt={`${currentSubsection.title} screenshot`}
                    caption={`${currentSubsection.title} overview`}
                  />
                )}

                {/* Steps */}
                {currentSubsection.steps && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-4">How to Use</h3>
                    <div className="space-y-3">
                      {currentSubsection.steps.map((step, index) => (
                        <div
                          key={index}
                          className="flex gap-4 p-4 rounded-lg border bg-card"
                        >
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                            {index + 1}
                          </div>
                          <div>
                            <h4 className="font-medium">{step.title}</h4>
                            <p className="text-sm text-muted-foreground">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Second Screenshot */}
                {currentSubsection.screenshots?.[1] && (
                  <Screenshot
                    id={currentSubsection.screenshots[1]}
                    alt={`${currentSubsection.title} detail screenshot`}
                    caption={`${currentSubsection.title} form/editor view`}
                  />
                )}
              </div>
            ) : (
              /* Welcome Screen */
              <div className="p-6">
                <div className="text-center py-12">
                  <Book className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h2 className="text-2xl font-bold mb-2">
                    Welcome to the Blueprint Desk User Guide
                  </h2>
                  <p className="text-muted-foreground max-w-md mx-auto mb-8">
                    Select a topic from the sidebar to learn how to use the platform
                    effectively.
                  </p>
                </div>

                {/* Quick Links */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {guideSections.map((section) => {
                    const Icon = section.icon
                    return (
                      <Card
                        key={section.id}
                        className="cursor-pointer hover:border-primary transition-colors"
                        onClick={() => {
                          setSelectedSection(section.id)
                          if (section.subsections.length > 0) {
                            setSelectedSubsection({
                              sectionId: section.id,
                              subsectionId: section.subsections[0].id,
                            })
                          }
                        }}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-center gap-2">
                            <Icon className="h-5 w-5 text-primary" />
                            <CardTitle className="text-base">{section.title}</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <CardDescription className="text-xs">
                            {section.description}
                          </CardDescription>
                          <Badge variant="secondary" className="mt-2">
                            {section.subsections.length} topics
                          </Badge>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              </div>
            )}
          </ScrollArea>
        </Card>
      </div>
    </div>
  )
}

